import { useEffect } from 'react';
import { configureAuthHandlers } from '@/lib/api-client';
import { authService } from '@/services/auth.service';
import { connectRoomSocket, disconnectRoomSocket, getRoomSocket } from '@/services/socket.service';
import { useAuthStore } from '@/stores/auth.store';

interface AuthProviderProps {
  children: React.ReactNode;
}

function ensureRoomSocket(token: string): void {
  const existing = getRoomSocket();
  if (existing) {
    existing.auth = { token };
    if (!existing.connected) {
      existing.connect();
    }
    return;
  }
  connectRoomSocket(token);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setInitialized = useAuthStore((s) => s.setInitialized);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    configureAuthHandlers({
      getAccessToken: () => useAuthStore.getState().accessToken,
      setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
      clearAuth: () => useAuthStore.getState().clearAuth(),
    });
  }, []);

  useEffect(() => {
    if (isInitialized) return;

    async function bootstrap() {
      const { accessToken } = useAuthStore.getState();

      try {
        if (accessToken) {
          const me = await authService.me();
          setAuth(me.user, accessToken);
          ensureRoomSocket(accessToken);
        } else {
          try {
            const refreshed = await authService.refresh();
            const me = await authService.me();
            setAuth(me.user, refreshed.tokens.accessToken);
            ensureRoomSocket(refreshed.tokens.accessToken);
          } catch {
            clearAuth();
          }
        }
      } catch {
        try {
          const refreshed = await authService.refresh();
          const me = await authService.me();
          setAuth(me.user, refreshed.tokens.accessToken);
          ensureRoomSocket(refreshed.tokens.accessToken);
        } catch {
          clearAuth();
        }
      } finally {
        setInitialized(true);
      }
    }

    bootstrap();
  }, [isInitialized, setAuth, clearAuth, setInitialized]);

  useEffect(() => {
    if (!isInitialized) return;

    if (isAuthenticated && accessToken) {
      ensureRoomSocket(accessToken);
      return;
    }

    disconnectRoomSocket();
  }, [isInitialized, isAuthenticated, accessToken]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-900">
        <div className="w-10 h-10 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
